import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Brand from "@/components/Brand";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, Upload, X } from "lucide-react";

// Category definitions (labels in sentence case, per Round 3 §5)
const CATEGORIES = [
    { v: "car_owner", l: "Car owners" },
    { v: "motorcycle_owner", l: "Motorcycle owners" },
    { v: "media", l: "Media" },
    { v: "brand", l: "Brands and partners" },
    { v: "guest", l: "Guests" },
];

// Grouped presentation reflecting distinct standing (Round 2 Decision B, refined in Round 3 §4)
const CATEGORY_GROUPS = [
    { label: "Membership pathways", keys: ["car_owner", "motorcycle_owner"] },
    { label: "Professional relationships", keys: ["media", "brand"] },
    { label: "By referral", keys: ["guest"] },
];

const MEDIA_TYPES = ["Photography", "Videography", "Editorial / Print", "Podcast", "Social / Content Creator"];
const BRAND_CATEGORIES = ["Automotive", "Apparel / Lifestyle", "Food & Beverage", "Hospitality", "Tech / Digital", "Other"];

const MAX_FILE_SIZE_MB = 8;
const MAX_FILES = 6;

const initial = {
    full_name: "",
    mobile: "",
    email: "",
    instagram: "",
    city: "",
    country: "",
    category: "",
    // Vehicle / Motorcycle
    make: "",
    model: "",
    year: "",
    build_description: "",
    // Media
    media_outlet: "",
    media_type: "",
    portfolio_url: "",
    audience_size: "",
    // Brand
    brand_name: "",
    brand_category: "",
    partnership_interest: "",
    website: "",
    // Guest
    reason: "",
    reference: "",
    // Common
    photos: [],
    agreed_terms: false,
};

function Field({ label, children, required, testid, hint }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="overline mb-2 block text-zinc-500">
                {label}
                {required && <span className="text-[var(--crewz-gold)]"> *</span>}
            </span>
            {children}
            {hint && <span className="font-body text-xs text-zinc-500 mt-1.5 block leading-relaxed">{hint}</span>}
        </label>
    );
}

const inputCls =
    "w-full bg-[var(--crewz-surface-1)] border border-white/10 focus:border-[var(--crewz-gold)] focus:outline-none text-white px-4 py-3 font-body text-sm placeholder:text-zinc-600 transition-colors";

function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function Membership() {
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(null);
    const fileInputRef = useRef(null);

    // Pre-select category from URL param (?category=car_owner)
    useEffect(() => {
        const paramCategory = searchParams.get("category");
        if (paramCategory && CATEGORIES.some((c) => c.v === paramCategory)) {
            setForm((prev) => ({ ...prev, category: paramCategory }));
        }
    }, [searchParams]);

    const update = (k) => (e) =>
        setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const cat = form.category;
    const isVehicle = cat === "car_owner" || cat === "motorcycle_owner";
    const isMedia = cat === "media";
    const isBrand = cat === "brand";
    const isGuest = cat === "guest";

    // Photo requirements per category
    const minPhotos = isVehicle ? 2 : isMedia ? 1 : isBrand ? 1 : 0;
    const photoLabel = isVehicle
        ? "Photographs"
        : isMedia
        ? "Samples of your work"
        : isBrand
        ? "Brand materials"
        : "Supporting images";
    const photoHint = isVehicle
        ? "At least two photographs. You can add up to six. Each up to 8 MB."
        : isMedia
        ? "At least one sample of your work. You can add up to six. Each up to 8 MB."
        : isBrand
        ? "At least one image — your brand mark or a piece of work that represents you. You can add up to six."
        : "Optional. Add any images that support your application.";

    const handleFiles = async (e) => {
        setError("");
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const remaining = MAX_FILES - form.photos.length;
        if (remaining <= 0) {
            setError(`Six photographs is the maximum.`);
            return;
        }
        const accepted = files.slice(0, remaining);
        const newItems = [];
        for (const file of accepted) {
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                setError(`"${file.name}" is larger than the ${MAX_FILE_SIZE_MB} MB limit.`);
                continue;
            }
            try {
                const dataUrl = await readFileAsDataURL(file);
                newItems.push({ name: file.name, type: file.type, size: file.size, dataUrl });
            } catch (err) {
                console.error(err);
            }
        }
        setForm((prev) => ({ ...prev, photos: [...prev.photos, ...newItems] }));
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removePhoto = (idx) => {
        setForm((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");

        if (!form.category) {
            setError("Please select a category before continuing.");
            return;
        }
        if (!form.agreed_terms) {
            setError("Please accept the standards before sending your application.");
            return;
        }
        if (form.photos.length < minPhotos) {
            setError(
                minPhotos === 1
                    ? "Please include at least one photograph."
                    : `Please include at least ${minPhotos} photographs.`
            );
            return;
        }

        setSubmitting(true);
        try {
            const { data } = await api.post("/applications", form);
            setDone(data);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // ---------------- Success state ----------------
    if (done) {
        return (
            <div className="min-h-screen bg-[var(--crewz-bg)]" data-testid="membership-success">
                <Navbar />
                <section className="px-6 md:px-12 py-32 max-w-2xl mx-auto text-center">
                    <CheckCircle2 className="mx-auto text-[var(--crewz-gold)]" size={56} />
                    <h1
                        className="font-display text-4xl md:text-5xl text-[var(--crewz-text)] mt-8 leading-[0.95]"
                        data-testid="membership-success-title"
                    >
                        Your application has been received.
                    </h1>
                    <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 mt-6 leading-relaxed font-light">
                        It will be reviewed individually. You will be contacted once a decision has been made.
                    </p>
                    <p
                        className="font-body text-sm text-zinc-500 mt-10 tracking-[0.22em] uppercase"
                        data-testid="membership-success-reference"
                    >
                        Reference · {done.id?.slice(0, 8)}
                    </p>
                </section>
                <Footer />
            </div>
        );
    }

    // ---------------- Main page ----------------
    return (
        <div className="min-h-screen bg-[var(--crewz-bg)]" data-testid="membership-page">
            <Navbar />

            {/* ============================================================
                OPENING BAND
                Hero + What membership is + What belonging asks
                Tighter vertical rhythm so all three read as one statement.
                ============================================================ */}
            <section
                className="px-6 md:px-12 lg:px-20 pt-24 pb-20 max-w-5xl mx-auto"
                data-testid="membership-opening-band"
            >
                {/* Hero */}
                <p className="overline mb-5">Membership</p>
                <h1
                    className="font-display text-5xl md:text-7xl text-[var(--crewz-text)] leading-[0.95]"
                    data-testid="membership-title"
                >
                    Belonging means more than joining.
                </h1>
                <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 mt-8 max-w-2xl font-light leading-relaxed">
                    <Brand /> is a community of car and motorcycle enthusiasts. Becoming a member means more than
                    filling out a form. It means sharing the community&apos;s passion, respecting its people, and
                    holding the same standards every member is expected to keep.
                </p>

                {/* What membership is */}
                <div className="mt-20" data-testid="membership-what-is">
                    <p className="overline mb-5">What membership is</p>
                    <div className="max-w-2xl space-y-5 font-body text-base md:text-lg text-[var(--crewz-text)]/85 font-light leading-relaxed">
                        <p>
                            <Brand /> brings together enthusiasts from a wide range of nationalities and cultural
                            backgrounds, united by a shared passion for automotive and motorcycle culture and a
                            commitment to the same standards.
                        </p>
                        <p>
                            What members have in common is not where they come from, what they own, or what they do. It
                            is how they treat the culture, and how they treat the people around them.
                        </p>
                        <p>
                            Membership is not decided by wealth, nationality, profession, popularity, or social status.
                            It is offered to people who are ready to respect the community and represent it well.
                        </p>
                    </div>
                </div>

                {/* What belonging asks */}
                <div className="mt-20" data-testid="membership-what-asks">
                    <p className="overline mb-5">What belonging asks</p>
                    <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 max-w-2xl font-light leading-relaxed">
                        Every member is expected to meet the same four standards. They apply to everyone equally,
                        without exception.
                    </p>
                    <ol className="mt-10 space-y-6 max-w-2xl list-none" data-testid="membership-standards">
                        <li className="font-body text-xl md:text-2xl text-[var(--crewz-text)] leading-snug font-light">
                            Follow Saudi law in every CrewZNatioN gathering, event, or activity.
                        </li>
                        <li className="font-body text-xl md:text-2xl text-[var(--crewz-text)] leading-snug font-light">
                            Respect the community’s members, guests, and organizers.
                        </li>
                        <li className="font-body text-xl md:text-2xl text-[var(--crewz-text)] leading-snug font-light">
                            Represent <Brand /> responsibly, both within the community and in public.
                        </li>
                        <li className="font-body text-xl md:text-2xl text-[var(--crewz-text)] leading-snug font-light">
                            Hold yourself to the same standard you expect from others.
                        </li>
                    </ol>
                    <p className="font-body text-sm md:text-base text-zinc-500 mt-10 max-w-2xl leading-relaxed">
                        These standards apply from the day someone becomes a member, and for as long as they represent
                        CrewZNatioN.
                    </p>
                </div>
            </section>

            {/* ============================================================
                WHO APPLIES
                Grouped presentation, distinct standing, equal respect.
                ============================================================ */}
            <section
                className="px-6 md:px-12 lg:px-20 py-20 max-w-5xl mx-auto border-t border-white/5"
                data-testid="membership-who-applies"
            >
                <p className="overline mb-5">Who applies</p>
                <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 max-w-2xl font-light leading-relaxed">
                    There are three ways to apply or connect with the community. Every applicant is welcomed with the
                    same respect.
                </p>

                <div className="mt-12 space-y-12 max-w-3xl">
                    {/* Membership pathways */}
                    <div data-testid="applies-group-pathways">
                        <p className="overline text-zinc-500 mb-5">Membership pathways</p>
                        <div className="space-y-5">
                            <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 leading-relaxed font-light">
                                <span className="text-[var(--crewz-text)]">Car owners</span> — for those whose
                                relationship to their car is part of their life, not just their transport.
                            </p>
                            <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 leading-relaxed font-light">
                                <span className="text-[var(--crewz-text)]">Motorcycle owners</span> — for riders who
                                feel the same way about two wheels.
                            </p>
                        </div>
                    </div>

                    {/* Professional relationships */}
                    <div data-testid="applies-group-professional">
                        <p className="overline text-zinc-500 mb-5">Professional relationships</p>
                        <div className="space-y-5">
                            <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 leading-relaxed font-light">
                                <span className="text-[var(--crewz-text)]">Media</span> — for journalists,
                                photographers, videographers, and creators who cover the community and its culture.
                            </p>
                            <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 leading-relaxed font-light">
                                <span className="text-[var(--crewz-text)]">Brands and partners</span> — for companies
                                whose products, services, or work connect naturally with the community.
                            </p>
                        </div>
                    </div>

                    {/* By referral */}
                    <div data-testid="applies-group-referral">
                        <p className="overline text-zinc-500 mb-5">By referral</p>
                        <div className="space-y-5">
                            <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 leading-relaxed font-light">
                                <span className="text-[var(--crewz-text)]">Guests</span> — for people introduced by a
                                current member. A referral supports an application, but does not guarantee acceptance.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================================================
                THE APPLICATION (form)
                ============================================================ */}
            <section
                className="px-6 md:px-12 lg:px-20 py-20 max-w-5xl mx-auto border-t border-white/5"
                data-testid="membership-application-section"
            >
                <p className="overline mb-5">The application</p>
                <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 max-w-2xl font-light leading-relaxed">
                    The form adjusts to the category you choose. You will only be asked what is relevant to your
                    application.
                </p>

                <form onSubmit={submit} id="membership-form" data-testid="membership-form" className="mt-10">
                    {/* Intake card */}
                    <div className="bg-[var(--crewz-surface-1)] border border-white/10 p-8 md:p-12">
                        {/* Your category */}
                        <div>
                            <h2 className="font-display text-2xl md:text-3xl text-[var(--crewz-text)]">Your category</h2>

                            <div className="mt-8 space-y-8" data-testid="category-selector">
                                {CATEGORY_GROUPS.map((group) => (
                                    <div key={group.label}>
                                        <p className="overline text-zinc-500 mb-4">{group.label}</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {group.keys.map((key) => {
                                                const c = CATEGORIES.find((x) => x.v === key);
                                                const selected = form.category === c.v;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={c.v}
                                                        onClick={() => setForm({ ...form, category: c.v })}
                                                        className={`p-4 border text-left transition-all duration-200 ${
                                                            selected
                                                                ? "border-[var(--crewz-gold)] bg-[var(--crewz-gold)]/10 text-[var(--crewz-gold)]"
                                                                : "border-white/10 text-zinc-300 hover:border-white/30"
                                                        }`}
                                                        data-testid={`category-tile-${c.v}`}
                                                        aria-pressed={selected}
                                                    >
                                                        <span className="font-body text-sm font-medium">{c.l}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {form.category && (
                            <>
                                {/* Your details */}
                                <div className="mt-12 pt-10 border-t border-white/10">
                                    <h2 className="font-display text-2xl md:text-3xl text-[var(--crewz-text)]">
                                        Your details
                                    </h2>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                        <Field label="Full name" required testid="field-full-name">
                                            <input
                                                type="text"
                                                required
                                                value={form.full_name}
                                                onChange={update("full_name")}
                                                className={inputCls}
                                                placeholder="Your name"
                                                data-testid="membership-full-name-input"
                                            />
                                        </Field>
                                        <Field label="Mobile number" required testid="field-mobile">
                                            <input
                                                type="tel"
                                                required
                                                value={form.mobile}
                                                onChange={update("mobile")}
                                                className={inputCls}
                                                placeholder="Mobile number"
                                                data-testid="membership-mobile-input"
                                            />
                                        </Field>
                                        <Field label="Email" required testid="field-email">
                                            <input
                                                type="email"
                                                required
                                                value={form.email}
                                                onChange={update("email")}
                                                className={inputCls}
                                                placeholder="Email address"
                                                data-testid="membership-email-input"
                                            />
                                        </Field>
                                        <Field label="Instagram handle" testid="field-instagram">
                                            <input
                                                type="text"
                                                value={form.instagram}
                                                onChange={update("instagram")}
                                                className={inputCls}
                                                placeholder="Instagram handle (optional)"
                                                data-testid="membership-instagram-input"
                                            />
                                        </Field>
                                        <Field label="City" required testid="field-city">
                                            <input
                                                type="text"
                                                required
                                                value={form.city}
                                                onChange={update("city")}
                                                className={inputCls}
                                                placeholder="City"
                                                data-testid="membership-city-input"
                                            />
                                        </Field>
                                        <Field label="Country" required testid="field-country">
                                            <input
                                                type="text"
                                                required
                                                value={form.country}
                                                onChange={update("country")}
                                                className={inputCls}
                                                placeholder="Country"
                                                data-testid="membership-country-input"
                                            />
                                        </Field>
                                    </div>
                                </div>

                                {/* Category-specific */}
                                <div className="mt-12 pt-10 border-t border-white/10">
                                    <h2 className="font-display text-2xl md:text-3xl text-[var(--crewz-text)]">
                                        {isVehicle && "Your machine"}
                                        {isMedia && "Your work"}
                                        {isBrand && "Your brand"}
                                        {isGuest && "Your reference"}
                                    </h2>

                                    {isVehicle && (
                                        <>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                                                <Field label="Make" required testid="field-make">
                                                    <input
                                                        type="text"
                                                        required
                                                        value={form.make}
                                                        onChange={update("make")}
                                                        className={inputCls}
                                                        placeholder="Make (e.g. Porsche)"
                                                        data-testid="membership-make-input"
                                                    />
                                                </Field>
                                                <Field label="Model" required testid="field-model">
                                                    <input
                                                        type="text"
                                                        required
                                                        value={form.model}
                                                        onChange={update("model")}
                                                        className={inputCls}
                                                        placeholder="Model (e.g. 911 GT3)"
                                                        data-testid="membership-model-input"
                                                    />
                                                </Field>
                                                <Field label="Year" required testid="field-year">
                                                    <input
                                                        type="text"
                                                        required
                                                        value={form.year}
                                                        onChange={update("year")}
                                                        className={inputCls}
                                                        placeholder="Year"
                                                        data-testid="membership-year-input"
                                                    />
                                                </Field>
                                            </div>
                                            <div className="mt-6">
                                                <Field label="Build description" required testid="field-build">
                                                    <textarea
                                                        required
                                                        value={form.build_description}
                                                        onChange={update("build_description")}
                                                        rows={4}
                                                        className={`${inputCls} resize-none`}
                                                        placeholder="Tell us about the vehicle, its modifications, and what makes it important to you."
                                                        data-testid="membership-build-input"
                                                    />
                                                </Field>
                                            </div>
                                        </>
                                    )}

                                    {isMedia && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                            <Field label="Outlet, channel, or publication" required testid="field-media-outlet">
                                                <input
                                                    type="text"
                                                    required
                                                    value={form.media_outlet}
                                                    onChange={update("media_outlet")}
                                                    className={inputCls}
                                                    placeholder="Outlet, channel, or publication"
                                                    data-testid="membership-media-outlet-input"
                                                />
                                            </Field>
                                            <Field label="Media type" required testid="field-media-type">
                                                <select
                                                    required
                                                    value={form.media_type}
                                                    onChange={update("media_type")}
                                                    className={inputCls}
                                                    data-testid="membership-media-type-select"
                                                >
                                                    <option value="">Select…</option>
                                                    {MEDIA_TYPES.map((t) => (
                                                        <option key={t} value={t}>
                                                            {t}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Field>
                                            <Field label="Link to your work" required testid="field-portfolio-url">
                                                <input
                                                    type="url"
                                                    required
                                                    value={form.portfolio_url}
                                                    onChange={update("portfolio_url")}
                                                    className={inputCls}
                                                    placeholder="Link to your work"
                                                    data-testid="membership-portfolio-url-input"
                                                />
                                            </Field>
                                            <Field label="Audience" required testid="field-audience-size">
                                                <input
                                                    type="text"
                                                    required
                                                    value={form.audience_size}
                                                    onChange={update("audience_size")}
                                                    className={inputCls}
                                                    placeholder="Approximate audience size and where"
                                                    data-testid="membership-audience-size-input"
                                                />
                                            </Field>
                                        </div>
                                    )}

                                    {isBrand && (
                                        <div className="mt-8">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <Field label="Brand or company" required testid="field-brand-name">
                                                    <input
                                                        type="text"
                                                        required
                                                        value={form.brand_name}
                                                        onChange={update("brand_name")}
                                                        className={inputCls}
                                                        placeholder="Brand or company name"
                                                        data-testid="membership-brand-name-input"
                                                    />
                                                </Field>
                                                <Field label="Brand category" required testid="field-brand-category">
                                                    <select
                                                        required
                                                        value={form.brand_category}
                                                        onChange={update("brand_category")}
                                                        className={inputCls}
                                                        data-testid="membership-brand-category-select"
                                                    >
                                                        <option value="">Select…</option>
                                                        {BRAND_CATEGORIES.map((b) => (
                                                            <option key={b} value={b}>
                                                                {b}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </Field>
                                                <Field label="Website" required testid="field-website">
                                                    <input
                                                        type="url"
                                                        required
                                                        value={form.website}
                                                        onChange={update("website")}
                                                        className={inputCls}
                                                        placeholder="Website"
                                                        data-testid="membership-website-input"
                                                    />
                                                </Field>
                                            </div>
                                            <div className="mt-6">
                                                <Field label="Nature of the collaboration" required testid="field-partnership">
                                                    <textarea
                                                        required
                                                        value={form.partnership_interest}
                                                        onChange={update("partnership_interest")}
                                                        rows={4}
                                                        className={`${inputCls} resize-none`}
                                                        placeholder="Tell us about your brand and the kind of collaboration you have in mind."
                                                        data-testid="membership-partnership-input"
                                                    />
                                                </Field>
                                            </div>
                                        </div>
                                    )}

                                    {isGuest && (
                                        <div className="mt-8 space-y-6">
                                            <Field label="Reason for joining" required testid="field-reason">
                                                <textarea
                                                    required
                                                    value={form.reason}
                                                    onChange={update("reason")}
                                                    rows={4}
                                                    className={`${inputCls} resize-none`}
                                                    placeholder="Tell us why you would like to join and how you came to know about CrewZNatioN."
                                                    data-testid="membership-reason-input"
                                                />
                                            </Field>
                                            <Field label="Referred by" required testid="field-reference">
                                                <input
                                                    type="text"
                                                    required
                                                    value={form.reference}
                                                    onChange={update("reference")}
                                                    className={inputCls}
                                                    placeholder="The name of the member who referred you"
                                                    data-testid="membership-reference-input"
                                                />
                                            </Field>
                                        </div>
                                    )}
                                </div>

                                {/* Photographs */}
                                <div className="mt-12 pt-10 border-t border-white/10">
                                    <h2 className="font-display text-2xl md:text-3xl text-[var(--crewz-text)]">
                                        {photoLabel}
                                        {minPhotos > 0 && <span className="text-[var(--crewz-gold)]"> *</span>}
                                    </h2>
                                    <p className="font-body text-sm text-zinc-400 mt-3 leading-relaxed">{photoHint}</p>

                                    <div className="mt-8">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={handleFiles}
                                            className="hidden"
                                            data-testid="membership-photos-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={form.photos.length >= MAX_FILES}
                                            className="inline-flex items-center gap-2 border border-white/20 text-zinc-200 hover:border-white/50 hover:text-white disabled:opacity-50 px-6 py-3 text-sm tracking-[0.18em] uppercase font-medium transition-colors"
                                            data-testid="membership-upload-btn"
                                        >
                                            <Upload size={16} />
                                            {form.photos.length === 0
                                                ? "Upload photographs"
                                                : `Add more (${form.photos.length}/${MAX_FILES})`}
                                        </button>
                                    </div>

                                    {form.photos.length > 0 && (
                                        <div
                                            className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-6"
                                            data-testid="photo-previews"
                                        >
                                            {form.photos.map((p, i) => (
                                                <div
                                                    key={i}
                                                    className="relative aspect-square overflow-hidden border border-white/10 group"
                                                    data-testid={`photo-preview-${i}`}
                                                >
                                                    <img
                                                        src={p.dataUrl}
                                                        alt={p.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removePhoto(i)}
                                                        className="absolute top-1 right-1 bg-black/80 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        aria-label="Remove photo"
                                                        data-testid={`photo-remove-${i}`}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Terms */}
                                <div className="mt-12 pt-10 border-t border-white/10">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={form.agreed_terms}
                                            onChange={update("agreed_terms")}
                                            className="mt-1 w-4 h-4 accent-[var(--crewz-gold)]"
                                            data-testid="membership-terms-checkbox"
                                        />
                                        <span className="font-body text-sm text-zinc-300 leading-relaxed">
                                            I have read and agree to the standards above.
                                        </span>
                                    </label>
                                </div>
                            </>
                        )}
                    </div>
                </form>
            </section>

            {/* ============================================================
                CLOSING BAND
                How applications are reviewed + Privacy notice + Before + Submit
                ============================================================ */}
            <section
                className="px-6 md:px-12 lg:px-20 py-20 max-w-5xl mx-auto border-t border-white/5"
                data-testid="membership-closing-band"
            >
                {/* How applications are reviewed */}
                <div className="max-w-3xl" data-testid="membership-how-reviewed">
                    <p className="overline mb-5">How applications are reviewed</p>
                    <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 font-light leading-relaxed">
                        Every application is read individually. There is no fixed timeline, but you will be contacted
                        once a decision has been made.
                    </p>
                </div>

                {/* Privacy notice */}
                <div className="max-w-3xl mt-16" data-testid="membership-privacy-notice">
                    <p className="overline mb-5">How we handle your information</p>
                    <div className="space-y-5 font-body text-base md:text-lg text-[var(--crewz-text)]/85 font-light leading-relaxed">
                        <p>
                            We ask for your name, contact details, city, country, category, and the information relevant
                            to your application. If you apply as an owner, we ask about your vehicle. If you apply as
                            media or a brand, we ask about your work. If you apply as a guest, we ask for the name of
                            the member who referred you. You may also upload photographs.
                        </p>
                        <p>
                            The information you share is used to review your application and, if your application is
                            accepted, to maintain your membership record.
                        </p>
                        <p>
                            Your application is stored securely and is seen only by a small number of authorised
                            administrators who review applications.
                        </p>
                        <p>
                            If your application is accepted, your information is kept as part of your membership record
                            for as long as you remain an active member. If your application is declined, your
                            information is kept for twelve months and then deleted, unless there is a specific reason to
                            keep it longer.
                        </p>
                        <p>
                            You may ask to see the information we hold about you, ask us to correct it, or ask us to
                            delete it once a decision has been made on your application.
                        </p>
                        <p>
                            To make any of these requests, please contact us using the details on our{" "}
                            <Link
                                to="/contact"
                                className="text-[var(--crewz-text)] underline decoration-white/30 underline-offset-4 hover:decoration-[var(--crewz-gold)] transition-colors"
                                data-testid="membership-privacy-contact-link"
                            >
                                Contact page
                            </Link>
                            .
                        </p>
                    </div>
                </div>

                {/* Before you send this + Submit (gated on category selection) */}
                {form.category && (
                    <div className="mt-20 max-w-3xl" data-testid="membership-before-submit">
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/85 font-light leading-relaxed">
                            Before sending this, take a moment to ask yourself —
                        </p>
                        <p className="font-display text-2xl md:text-3xl text-[var(--crewz-text)] mt-3 leading-snug">
                            Am I ready to represent <Brand />?
                        </p>

                        {error && (
                            <p
                                className="mt-8 text-sm text-red-400 font-body"
                                data-testid="membership-error"
                                role="alert"
                            >
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            form="membership-form"
                            disabled={submitting}
                            className="mt-10 inline-flex items-center gap-2 bg-[var(--crewz-gold)] text-[var(--crewz-bg)] hover:bg-[var(--crewz-gold-soft)] disabled:opacity-60 px-8 py-4 text-sm tracking-[0.22em] uppercase font-bold transition-colors"
                            data-testid="membership-submit-btn"
                        >
                            {submitting ? "Sending…" : "Send for review"}
                        </button>
                    </div>
                )}
            </section>

            {/* Community expression — positioned exactly 64px above the
                footer, horizontally centered within the content column.
                All surrounding content is unchanged. */}
            <div
                data-testid="membership-community-expression-wrapper"
                className="relative mx-auto max-w-4xl px-6 md:px-12 lg:px-20"
                style={{
                    position: "relative",
                    paddingTop: "32px",
                    paddingBottom: "128px",
                    boxSizing: "border-box",
                    transform: "translateX(0px)",
                }}
            >
                <p
                    data-testid="membership-community-expression"
                    className="font-display text-3xl md:text-4xl lg:text-5xl text-[var(--crewz-text)] leading-none whitespace-nowrap"
                    style={{
                        position: "absolute",
                        bottom: "64px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        margin: 0,
                        padding: 0,
                        textAlign: "center",
                    }}
                >
                    Eat <span className="text-[var(--crewz-gold)]">·</span> Sleep <span className="text-[var(--crewz-gold)]">·</span> CrewZ <span className="text-[var(--crewz-gold)]">·</span> Repeat
                </p>
            </div>

            <Footer />
        </div>
    );
}
